import { useEffect, useMemo, useRef } from 'react';
import type { MouseEvent as ReactMouseEvent, PointerEvent as ReactPointerEvent } from 'react';

// PARA-135 (A-2, AC-6, AC-7) — press-and-hold без внешних зависимостей.
// Пороги — решение заказчика РЕШ-6.
export const LONG_PRESS_MS = 500;
export const MOVE_CANCEL_PX = 10;

interface UseLongPressOptions {
  onLongPress: () => void;
  onTap: () => void;
}

export interface LongPressHandlers {
  onPointerDown: (event: ReactPointerEvent) => void;
  onPointerMove: (event: ReactPointerEvent) => void;
  onPointerUp: () => void;
  onPointerCancel: () => void;
  onPointerLeave: () => void;
  onClick: (event: ReactMouseEvent) => void;
}

/**
 * Возвращает набор хендлеров для навешивания на интерактивный элемент.
 *
 * АР-2: нативный элемент выдаёт `click` и после удержания, поэтому хук держит
 * ref-флаг `didFire` и глотает такой клик — иначе на одном жесте сработали бы
 * и смена статуса, и открытие редактирования (AC-7).
 *
 * АР-3: отмена по смещению делается здесь, а не через `touch-action: none` —
 * тот убил бы вертикальный скролл списка.
 */
export function useLongPress({ onLongPress, onTap }: UseLongPressOptions): LongPressHandlers {
  const callbacksRef = useRef({ onLongPress, onTap });
  // H-4 (Z-4): присваивание в теле рендера — побочный эффект во время рендера,
  // небезопасный при прерываемом рендере React 18+. Эффект выполняется до
  // любого pointer-события (те возможны только после монтирования).
  useEffect(() => {
    callbacksRef.current = { onLongPress, onTap };
  }, [onLongPress, onTap]);

  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const originRef = useRef<{ x: number; y: number } | null>(null);
  /** Удержание уже сработало — следующий click подавляется. */
  const didFireRef = useRef(false);
  /** Жест прерван (смещение/скролл/pointercancel) — не тап и не удержание. */
  const cancelledRef = useRef(false);
  /** Указатель прижат: между pointerdown и pointerup/pointercancel. */
  const isPressedRef = useRef(false);

  const clearTimer = (): void => {
    if (timerRef.current !== null) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  };

  // Таймер обязан сниматься при размонтировании: иначе удержание, начатое
  // перед закрытием экрана, откроет редактирование уже мёртвой карточки.
  useEffect(() => () => clearTimer(), []);

  return useMemo<LongPressHandlers>(
    () => ({
      onPointerDown: (event) => {
        // H-5 (Z-5): удержание стартует только основной кнопкой основного
        // указателя — правый/средний клик и второй палец при мультитаче жест
        // не запускают. `isPrimary === false` (а не `!isPrimary`): у не-pointer
        // событий свойства нет, и трактовать его отсутствие как «вторичный»
        // нельзя — это отключило бы жест целиком.
        if (event.button !== 0 || event.isPrimary === false) {
          return;
        }
        clearTimer();
        didFireRef.current = false;
        cancelledRef.current = false;
        isPressedRef.current = true;
        originRef.current = { x: event.clientX, y: event.clientY };
        timerRef.current = setTimeout(() => {
          timerRef.current = null;
          didFireRef.current = true;
          callbacksRef.current.onLongPress();
        }, LONG_PRESS_MS);
      },
      onPointerMove: (event) => {
        const origin = originRef.current;
        if (origin === null) {
          return;
        }
        const distance = Math.hypot(event.clientX - origin.x, event.clientY - origin.y);
        if (distance > MOVE_CANCEL_PX) {
          cancelledRef.current = true;
          clearTimer();
        }
      },
      onPointerUp: () => {
        // Жест завершён пользователем: всё, что придёт после (pointerout /
        // pointerleave на touch), к отмене отношения уже не имеет.
        isPressedRef.current = false;
        clearTimer();
      },
      onPointerCancel: () => {
        isPressedRef.current = false;
        cancelledRef.current = true;
        clearTimer();
      },
      // H-1 (БЛОКЕР): отменять можно ТОЛЬКО пока указатель прижат.
      // У touch-указателя действует implicit pointer capture, поэтому браузер
      // выдаёт pointerdown → pointerup → pointerout → pointerleave → click:
      // pointerleave приходит между pointerup и click НА КАЖДОМ обычном тапе.
      // Безусловная отмена здесь глушила бы onTap на любом касании пальцем —
      // то есть AC-1/AC-2/AC-4 не работали бы на телефоне, где жест и живёт.
      // Таймер снимаем в любом случае: уход указателя удержание прекращает.
      onPointerLeave: () => {
        if (isPressedRef.current) {
          isPressedRef.current = false;
          cancelledRef.current = true;
        }
        clearTimer();
      },
      onClick: (event) => {
        const suppressed = didFireRef.current || cancelledRef.current;
        didFireRef.current = false;
        cancelledRef.current = false;
        isPressedRef.current = false;
        originRef.current = null;
        if (suppressed) {
          event.preventDefault();
          return;
        }
        callbacksRef.current.onTap();
      },
    }),
    [],
  );
}
